// Pure team-region scoping logic shared by the API (list-endpoint enforcement)
// and the admin client (tree building / closure display).
//
// Model (see docs/superpowers/specs/2026-07-23-team-region-hierarchy-design.md):
//   - regions form a tree via parent_id; assigning a region grants it AND its
//     entire subtree, at arbitrary depth;
//   - rows are tagged by team_region_id (uuid FK), never by name;
//   - companies carry their own team_region_id; drivers/assets inherit the
//     company's when company_id is set, else carry their own;
//   - an admin user's empty assignment list means unrestricted;
//   - rows with no effective region are hidden from restricted members and
//     from any specific-region view (strict semantics).

export type TeamRegionNode = { id: string; parent_id: string | null };

export type TeamRegionSource = {
  company_id?: string | null;
  team_region_id?: string | null;
  company?: { team_region_id?: string | null } | null;
};

export function effectiveTeamRegion(row: TeamRegionSource): string | null {
  if (row.company_id != null) return row.company?.team_region_id ?? null;
  return row.team_region_id ?? null;
}

// Self + all descendants of rootIds within `nodes` (breadth-first). Cycle-safe
// via the seen set — the DB trigger forbids cycles, this just keeps the pure
// function total on any input. Root ids are included even when absent from
// `nodes`: an unknown id then simply matches no rows, the same net effect the
// old name-based scope had for an unknown name.
export function expandTeamRegionIds(rootIds: string[], nodes: TeamRegionNode[]): string[] {
  const childrenByParent = new Map<string, string[]>();
  for (const n of nodes) {
    if (n.parent_id === null) continue;
    const list = childrenByParent.get(n.parent_id) ?? [];
    list.push(n.id);
    childrenByParent.set(n.parent_id, list);
  }
  const seen = new Set<string>();
  const queue = [...rootIds];
  for (let i = 0; i < queue.length; i++) {
    const id = queue[i]!;
    if (seen.has(id)) continue;
    seen.add(id);
    for (const child of childrenByParent.get(id) ?? []) queue.push(child);
  }
  return [...seen];
}

// Would setting `parentId` on `regionId` create a cycle (including
// self-parenting)? Friendly pre-check for the PATCH route — the DB trigger
// (team_regions_no_cycle) is the authoritative backstop.
export function wouldCreateCycle(
  regionId: string,
  parentId: string | null,
  nodes: TeamRegionNode[],
): boolean {
  const parentOf = new Map(nodes.map((n) => [n.id, n.parent_id]));
  const hops = new Set<string>();
  let cursor: string | null = parentId;
  while (cursor !== null) {
    if (cursor === regionId) return true;
    if (hops.has(cursor)) return false; // pre-existing cycle elsewhere — not introduced by this edit
    hops.add(cursor);
    cursor = parentOf.get(cursor) ?? null;
  }
  return false;
}

export type TeamRegionScope = { ok: true; regionIds: string[] | null } | { ok: false };

// `allowedIds` is the member's CLOSURE (assigned + all descendants), already
// expanded by the caller — adminAuth expands via the team_region_descendants
// RPC. Empty allowedIds = unrestricted. When a specific region is requested
// the result is [requestedId] only; callers widen it to that region's own
// subtree with expandTeamRegionIds (see apps/api/src/lib/regionScope.ts) —
// the subtree of an in-closure region is always a subset of the closure, so
// that widening can never grant extra access.
export function resolveTeamRegionScope(
  allowedIds: string[],
  requestedId: string | null,
): TeamRegionScope {
  const trimmed = (requestedId ?? '').trim();
  const want = trimmed === '' ? null : trimmed;
  if (allowedIds.length === 0) return { ok: true, regionIds: want ? [want] : null };
  if (!want) return { ok: true, regionIds: allowedIds };
  if (!allowedIds.includes(want)) return { ok: false };
  return { ok: true, regionIds: [want] };
}

export function matchesTeamRegionScope(
  effectiveId: string | null,
  regionIds: string[] | null,
): boolean {
  if (regionIds === null) return true;
  if (effectiveId === null) return false;
  return regionIds.includes(effectiveId);
}
